import type { NombreIcono } from '../../componentes/iconos';
import type { EstadoChip } from '../../componentes/Chip';
import type { ConflictoSinConexion, GrupoDeAcceso, Lectura, MetodoLectura, MotivoLectura, ResultadoLectura } from '../../api/ops/pases';
import type { Permisos } from '../../permisos/permisos';

/**
 * Reglas de la Puerta del panel (6.3 Preparar, 6.4 Conflictos, 6.5 Registro; `diseno/pases-acceso/`), sin React para
 * probarlas sueltas. La barra enseña solo los apartados que la persona puede ver (G778): Preparar con
 * `pases.paquetes.gestionar`, Conflictos con `pases.conflictos.revisar` y Registro con `pases.escaneos.ver` o, en cifras,
 * `pases.escaneos.ver_agregado` (el Socio).
 */
export type ApartadoPuerta = 'preparar' | 'conflictos' | 'registro';

export const APARTADOS_PUERTA: readonly { clave: ApartadoPuerta; etiqueta: string; icono: NombreIcono; permisos: readonly string[] }[] = [
    { clave: 'preparar', etiqueta: 'Preparar', icono: 'package', permisos: ['pases.paquetes.gestionar'] },
    { clave: 'conflictos', etiqueta: 'Conflictos', icono: 'git-compare', permisos: ['pases.conflictos.revisar'] },
    { clave: 'registro', etiqueta: 'Registro', icono: 'list-checks', permisos: ['pases.escaneos.ver', 'pases.escaneos.ver_agregado'] },
];

export function apartadosVisibles(permisos: Pick<Permisos, 'puede'>): typeof APARTADOS_PUERTA {
    return APARTADOS_PUERTA.filter((a) => a.permisos.some((p) => permisos.puede(p)));
}

/** El Registro en cifras: quien solo tiene `pases.escaneos.ver_agregado` (el Socio) ve `getScanSummary`, sin filas. */
export function registroSoloEnCifras(permisos: Pick<Permisos, 'puede'>): boolean {
    return !permisos.puede('pases.escaneos.ver') && permisos.puede('pases.escaneos.ver_agregado');
}

/** Motivo de cada lectura (`ScanReason`): texto corto, frase larga e icono, como en el diseño de 6.5. */
export const MOTIVOS: Readonly<Record<MotivoLectura, { corto: string; largo: string; icono: NombreIcono }>> = {
    ok: { corto: 'Entró', largo: 'Pase válido y primera lectura.', icono: 'check' },
    reentry: { corto: 'Volvió a entrar', largo: 'Tipo de pase con varias entradas.', icono: 'check-check' },
    admitted_override: { corto: 'Admitido igualmente', largo: 'Era ámbar y se admitió con motivo.', icono: 'check' },
    already_inside: { corto: 'Ya estaba dentro', largo: 'El pase ya tenía una llegada.', icono: 'triangle-alert' },
    concurrent_arrival: { corto: 'Otra tablet a la vez', largo: 'Otra tablet lo registró antes, por segundos.', icono: 'triangle-alert' },
    outside_window: { corto: 'Fuera de horario', largo: 'Llegó fuera de la ventana de validez.', icono: 'clock' },
    list_cutoff: { corto: 'Pasada la hora límite', largo: 'Llegó después de la hora límite de su lista.', icono: 'clock' },
    issued_after_package: { corto: 'Posterior al paquete', largo: 'Pase auténtico emitido después de la última actualización de la tablet.', icono: 'hourglass' },
    not_found: { corto: 'No encontrado', largo: 'El código no corresponde a ningún pase.', icono: 'search' },
    bad_signature: { corto: 'Firma no válida', largo: 'El QR no lo ha firmado este negocio: es falso o está dañado.', icono: 'shield-alert' },
    other_business: { corto: 'De otro negocio', largo: 'El pase es de otro negocio.', icono: 'store' },
    voided: { corto: 'Anulado', largo: 'El pase está anulado.', icono: 'ban' },
    expired: { corto: 'Caducado', largo: 'Pasaron más de 12 h desde el fin de su ventana.', icono: 'calendar-x' },
    key_revoked: { corto: 'Clave revocada', largo: 'Firmado con una clave revocada.', icono: 'shield-off' },
    undone: { corto: 'Llegada deshecha', largo: 'Lectura que registra un deshacer: no es verde, ámbar ni rojo y no cuenta en esos recuentos.', icono: 'undo-2' },
};

export const METODOS: Readonly<Record<MetodoLectura, string>> = {
    qr: 'QR',
    typed_code: 'Código tecleado',
    name_search: 'Búsqueda por nombre',
    manual: 'Marcado a mano',
};

/** Color de la lectura; la del deshacer (`result` nulo, G416, G421) va sin color. */
export function colorDeLectura(resultado: ResultadoLectura | null | undefined): { chip: EstadoChip; clase: string; icono: NombreIcono | null } {
    switch (resultado) {
        case 'green':
            return { chip: 'confirmed', clase: 'verde', icono: 'check' };
        case 'amber':
            return { chip: 'pending', clase: 'ambar', icono: 'triangle-alert' };
        case 'red':
            return { chip: 'problem', clase: 'rojo', icono: 'x' };
        default:
            return { chip: 'neutral', clase: 'neutra', icono: 'undo-2' };
    }
}

/** Filtro por motivo del Registro (G785): todos los motivos del contrato, en el orden del diseño. */
export const FILTRO_MOTIVOS = Object.keys(MOTIVOS) as MotivoLectura[];

/** La línea de debajo del nombre en el Registro: el tipo del pase en verde; el motivo (y quién decidió) en los demás. */
export function lineaDeLectura(l: Lectura): string {
    if (l.reason === 'ok') {
        return l.pass_label ?? MOTIVOS.ok.corto;
    }
    const partes = [MOTIVOS[l.reason].corto];
    if (l.decision === 'admitted') {
        partes.push(`admitido por ${l.by?.member_name ?? 'el equipo'}`);
    } else if (l.decision === 'rejected') {
        partes.push(`rechazado por ${l.by?.member_name ?? 'el equipo'}`);
    }
    if (l.reason === 'undone' && l.decision_reason) {
        partes.push(l.decision_reason);
    }
    return partes.join(' · ');
}

/** Nombre de la fila: el titular; sin pase, «Pase desconocido»; sin nombre, el tipo del pase. */
export function nombreDeLectura(l: Pick<Lectura, 'pass_id' | 'holder_name' | 'pass_label'>): string {
    if (l.pass_id === null || l.pass_id === undefined) {
        return 'Pase desconocido';
    }
    return l.holder_name ?? l.pass_label ?? 'Pase sin nombre';
}

/** Quién y cómo: la tablet (o «Desde el panel») y el método con quien validó. */
export function origenDeLectura(l: Lectura): { donde: string; como: string } {
    const quien = l.by?.member_name ?? null;
    return { donde: l.by?.device_name ?? (l.by?.device_id ? 'Tablet' : 'Desde el panel'), como: [METODOS[l.method], quien].filter((p) => p !== null).join(' · ') };
}

export const CONFLICTOS: Readonly<Record<ConflictoSinConexion['kind'], { titulo: string; texto: string; mantener: [string, string] }>> = {
    duplicate_arrival: {
        titulo: 'Entró en dos tablets',
        texto: 'El mismo pase se admitió en dos tablets. Ganó la primera lectura por hora: la otra quedó como ámbar.',
        mantener: ['Mantener la primera llegada', 'Entró una vez. La otra lectura sigue en el registro como ámbar.'],
    },
    voided_before_arrival: {
        titulo: 'Entró con el pase anulado',
        texto: 'Una tablet sin conexión admitió un pase que ya estaba anulado en el servidor.',
        mantener: ['Mantener la llegada', 'Cuenta como dentro aunque el pase estuviera anulado.'],
    },
    undone_while_offline: {
        titulo: 'Llegada deshecha mientras tanto',
        texto: 'La llegada se deshizo mientras otra tablet, sin conexión, la usaba.',
        mantener: ['Mantener la llegada', 'La admisión de la tablet cuenta: la persona está dentro.'],
    },
};

export const DESHACER_CONFLICTO: [string, string] = ['Deshacer la llegada', 'Vuelve la plaza al aforo y se revierte la comisión del RRPP, si la hay.'];

/** La nota de la revisión: de 10 a 500 caracteres (`ConflictResolution.note`). */
export function notaValida(nota: string): boolean {
    const n = nota.trim().length;
    return n >= 10 && n <= 500;
}

/** El motivo de revocar un paquete (`Reason`): de 10 a 500 caracteres, como el resto de motivos. */
export const motivoValido = notaValida;

/**
 * El grupo que se elige al entrar (G782): el que está en curso; si no, el próximo; si no, el último que pasó. `null` si no
 * hay ninguno.
 */
export function grupoPorDefecto(grupos: readonly GrupoDeAcceso[], ahora: Date): GrupoDeAcceso | null {
    const t = ahora.getTime();
    const vivos = grupos.filter((g) => g.status !== 'cancelled');
    const enCurso = vivos.find((g) => Date.parse(g.valid_from) <= t && Date.parse(g.valid_to) > t);
    if (enCurso !== undefined) {
        return enCurso;
    }
    const proximo = vivos.find((g) => Date.parse(g.valid_from) > t);
    return proximo ?? vivos.at(-1) ?? grupos.at(-1) ?? null;
}

/** Origen del grupo en el selector (`ref.type`): evento, jornada de sala, cita… */
export function origenDeGrupo(g: GrupoDeAcceso): string {
    switch (g.ref.type) {
        case 'evento':
            return 'Evento';
        case 'sala_dia':
        case 'jornada':
            return 'Jornada';
        case 'cita':
            return 'Cita';
        default:
            return 'Acceso';
    }
}

const FECHA = new Intl.DateTimeFormat('es-ES', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const HORA = new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit' });

export const fechaCorta = (iso: string | null | undefined): string => (iso ? FECHA.format(new Date(iso)) : '—');
export const horaCorta = (iso: string | null | undefined): string => (iso ? HORA.format(new Date(iso)) : '—');

/** Texto de una opción del selector: «Evento · Miércoles de cante · mié 14 oct, 21:00». */
export function opcionDeGrupo(g: GrupoDeAcceso): string {
    const estado = g.status === 'cancelled' ? ' (anulado)' : g.status === 'closed' ? ' (cerrado)' : '';
    return `${origenDeGrupo(g)} · ${g.label}${estado} · ${fechaCorta(g.valid_from)}`;
}

/** Estado de un paquete en palabras (`DoorPackageStatus`). */
export const ESTADO_PAQUETE: Readonly<Record<string, { texto: string; chip: EstadoChip }>> = {
    prepared: { texto: 'Sin descargar', chip: 'pending' },
    downloaded: { texto: 'Descargado', chip: 'confirmed' },
    synced: { texto: 'Al día', chip: 'confirmed' },
    expired: { texto: 'Caducado', chip: 'neutral' },
    revoked: { texto: 'Revocado', chip: 'problem' },
    wiped: { texto: 'Borrado', chip: 'neutral' },
};
